import { jsx } from 'react/jsx-runtime';
import { noop } from '@fabric-app/utils/guards/noop';
import { useState, useRef, useEffect, createContext, useContext } from 'react';

const AudioContext = /* @__PURE__ */ createContext({
  element: null,
  currentTime: 0,
  duration: 0,
  isPlaying: false,
  isReady: false,
  scrub: noop,
  togglePlay: noop
});
const useAudioContext = () => useContext(AudioContext);
const AudioContextProvider = ({ audioElement, preloadAudioElement, children }) => {
  const [isReady, setIsReady] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const durationRef = useRef(duration);
  durationRef.current = duration;
  const togglePlay = () => {
    if (!audioElement) return;
    if (isPlaying) {
      audioElement.pause();
    } else {
      audioElement.play();
    }
  };
  const scrub = (time) => {
    if (!audioElement) return;
    audioElement.currentTime = time;
  };
  useEffect(() => {
    if (!preloadAudioElement) return;
    const onDurationChange = () => {
      setDuration(isNaN(preloadAudioElement.duration) ? 0 : Math.max(preloadAudioElement.duration, 0));
      setIsReady(true);
    };
    preloadAudioElement.addEventListener("durationchange", onDurationChange);
    preloadAudioElement.addEventListener("loadedmetadata", onDurationChange);
    preloadAudioElement.addEventListener("load", onDurationChange);
    return () => {
      preloadAudioElement.removeEventListener("durationchange", onDurationChange);
      preloadAudioElement.removeEventListener("loadedmetadata", onDurationChange);
      preloadAudioElement.removeEventListener("load", onDurationChange);
    };
  }, [
    preloadAudioElement
  ]);
  useEffect(() => {
    if (!audioElement) return;
    const onPlay = () => setIsPlaying(true);
    const onPause = () => setIsPlaying(false);
    const onTimeUpdate = () => {
      setCurrentTime(isNaN(audioElement.currentTime) ? 0 : Math.max(audioElement.currentTime, 0));
    };
    const onEnded = () => {
      setIsPlaying(false);
      setCurrentTime(durationRef.current);
    };
    audioElement.addEventListener("play", onPlay);
    audioElement.addEventListener("pause", onPause);
    audioElement.addEventListener("timeupdate", onTimeUpdate);
    audioElement.addEventListener("ended", onEnded);
    return () => {
      audioElement.removeEventListener("play", onPlay);
      audioElement.removeEventListener("pause", onPause);
      audioElement.removeEventListener("timeupdate", onTimeUpdate);
      audioElement.removeEventListener("ended", onEnded);
    };
  }, [
    audioElement
  ]);
  return /* @__PURE__ */ jsx(AudioContext.Provider, {
    value: {
      element: audioElement,
      currentTime,
      duration,
      isPlaying,
      isReady,
      scrub,
      togglePlay
    },
    children
  });
};

export { AudioContextProvider, useAudioContext };
//# sourceMappingURL=AudioProvider.js.map
